/** Form for creating or updating a blocked time slot for a vet. */

import { z } from 'zod'

import { clinicIdSchema, wallClockToUtcIso } from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Button } from '@/frontend/components/ui/button'
import { useCreateBlockedSlot } from '@/frontend/features/appointments/use-create-blocked-slot'
import { useUpdateBlockedSlot } from '@/frontend/features/appointments/use-update-blocked-slot'

const blockedSlotFormSchema = z
  .object({
    staffId: z.string().min(1, 'Please select a vet'),
    clinicId: clinicIdSchema,
    startDate: z.string().min(1, 'Please select a start date'),
    startTime: z.string().min(1, 'Please select a start time'),
    endDate: z.string().min(1, 'Please select an end date'),
    endTime: z.string().min(1, 'Please select an end time'),
    note: z.string(),
  })
  .refine((v) => `${v.endDate}T${v.endTime}` > `${v.startDate}T${v.startTime}`, {
    message: 'End must be after start',
    path: ['endTime'],
  })

export type BlockedSlotFormValues = z.infer<typeof blockedSlotFormSchema>

interface BlockedSlotFormProps {
  defaultValues: Partial<BlockedSlotFormValues>
  timeZone: string
  blockedSlotId?: string
  onSuccess?: () => void
  onCancel?: () => void
}

export function BlockedSlotForm({
  defaultValues,
  timeZone,
  blockedSlotId,
  onSuccess,
  onCancel,
}: BlockedSlotFormProps) {
  const { mutateAsync: createBlockedSlot } = useCreateBlockedSlot()
  const { mutateAsync: updateBlockedSlot } = useUpdateBlockedSlot()

  const form = useAppForm({
    defaultValues: defaultValues as BlockedSlotFormValues,
    validators: { onSubmit: blockedSlotFormSchema },
    onSubmit: async ({ value, formApi }) => {
      try {
        const startIso = wallClockToUtcIso(value.startDate, value.startTime, timeZone)
        const endIso = wallClockToUtcIso(value.endDate, value.endTime, timeZone)
        const timesAndNote = {
          startTime: startIso,
          endTime: endIso,
          note: value.note || null,
        }
        if (blockedSlotId) {
          await updateBlockedSlot({ id: blockedSlotId, ...timesAndNote })
        } else {
          await createBlockedSlot({
            staffId: value.staffId,
            clinicId: value.clinicId,
            ...timesAndNote,
          })
        }
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to create block',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <p className="text-muted-foreground mb-3">
          Blocked time won't appear as available for public booking or be suggested for internal
          appointments. Use it for things like admin time, training, meetings, or events.
        </p>
        <div className="flex flex-col gap-3">
          <form.GlobalError />
          {!blockedSlotId && (
            <>
              <form.Field name="staffId">
                {(field) => (
                  <FieldWrapper
                    field={field}
                    label="Vet"
                    render={({ id, disabled }) => (
                      <StaffSearch
                        id={id}
                        value={field.state.value}
                        onChange={(staff) => field.handleChange(staff?.id ?? '')}
                        onBlur={field.handleBlur}
                        placeholder="Select vet"
                        disabled={disabled}
                        aria-invalid={!!field.state.meta.errors.length}
                      />
                    )}
                  />
                )}
              </form.Field>
              <form.Field name="clinicId">
                {(field) => (
                  <FieldWrapper
                    field={field}
                    label="Clinic"
                    render={({ id, disabled }) => (
                      <ClinicSearch
                        id={id}
                        value={field.state.value}
                        onChange={(clinicId) => clinicId && field.handleChange(clinicId)}
                        onBlur={field.handleBlur}
                        placeholder="Select clinic"
                        disabled={disabled}
                        aria-invalid={!!field.state.meta.errors.length}
                      />
                    )}
                  />
                )}
              </form.Field>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <form.AppField name="startDate">
              {(field) => <field.InputField label="Start date" type="date" />}
            </form.AppField>
            <form.AppField name="startTime">
              {(field) => <field.InputField label="Start time" type="time" />}
            </form.AppField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <form.AppField name="endDate">
              {(field) => <field.InputField label="End date" type="date" />}
            </form.AppField>
            <form.AppField name="endTime">
              {(field) => <field.InputField label="End time" type="time" />}
            </form.AppField>
          </div>
          <form.AppField name="note">
            {(field) => (
              <field.InputField label="Note (optional)" placeholder="e.g. Training, Holiday" />
            )}
          </form.AppField>
        </div>
        <div className="flex justify-end gap-2 pt-3 mt-2">
          {onCancel && (
            <Button variant="ghost" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton>{blockedSlotId ? 'Save changes' : 'Block time'}</form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}
